import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { getUtilityActivationFeedbackTags } from 'common/utility/helpers';

import { SR_API_ACTION_UTILITY_ACTIVATION_CREATE_FEEDBACK } from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';

import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import useApiState from 'client/hooks/useApiState';

import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

const getFormFields = ({ values = {}, formikBag = {} } = {}) => {
  return [
    {
      id: 'rating',
      type: 'star_rating',
      validations: ['required', 'positiveNumber'],
      properties: {
        noMargin: true,
        onChange: (newValue) => {
          if ((newValue < 5 && values.rating >= 5) || (newValue >= 5 && values.rating < 5)) {
            formikBag.setFieldValue('tags', []);
          }
        },
      },
    },
    showFieldIf(values.rating, {
      id: 'tags',
      type: 'tags',
      properties: {
        noMargin: true,
        choices: getUtilityActivationFeedbackTags(values.rating).map((tag) => ({
          value: tag,
          label: tag,
        })),
        justifyContent: 'center',
      },
      validations:
        values.rating < 5 && !values.notes ? ['nonEmptyArray::Please choose a reason'] : [],
    }),
    showFieldIf(values.rating, {
      id: 'notes',
      title: '',
      type: 'long_text',
      properties: {
        noMargin: true,
        placeholder: 'Share your feedback',
        style: {
          width: '100%',
          height: '100px',
        },
      },
    }),
  ];
};

const FEEDBACK_RATING_TO_LABEL_MAPPING = {
  1: 'Very Poor. What could improve?',
  2: 'Not so good. What could improve?',
  3: 'Only average. What could improve?',
  4: 'Pretty good! What could improve?',
  5: 'Amazing! What did you like?',
};

const UPUtilityActivationFeedbackForm = ({ utilityActivationId, onSubmit }) => {
  const dispatch = useDispatch();
  const createFeedbackApiState = useApiState(SR_API_ACTION_UTILITY_ACTIVATION_CREATE_FEEDBACK);

  const _onSubmit = (formValues) => {
    const feedbackTags = getUtilityActivationFeedbackTags(formValues.rating);
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_CREATE_FEEDBACK,
      payload: {
        utilityActivationId,
        feedback: {
          rating: formValues.rating,
          tags: formValues.tags ? formValues.tags.filter((tag) => feedbackTags.includes(tag)) : [],
          notes: formValues.notes || null,
        },
      },
    }).then(onSubmit);
  };

  return (
    <Formik
      validate={(formValues) => {
        const formFields = getFormFields({ values: formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      initialValues={createInitialValues({ formFields: getFormFields() })}
      onSubmit={_onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({ formikBag, values: formikBag.values });
        const getFormFieldsById = createFormFieldsByIdGetter(formFields);

        return (
          <Form>
            <SrFlex direction="col" align="center">
              <img
                src="/images/star-yellow-bg.png"
                alt="star"
                style={{
                  width: '99px',
                  height: '108px',
                }}
              />
              <SrBox textAlign="center" width="320px" margin="34px auto 12px">
                <SrText font="greycliff" weight="800" size="21" lineHeight="30px" align="center">
                  How helpful is this utility setup tool for your new home?
                </SrText>
              </SrBox>
              <SrFormikField
                formField={getFormFieldsById('rating')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />

              {formikBag.values.rating && (
                <SrBox padding="17px 0 16px">
                  <SrText font="greycliff" size="16" weight="700">
                    {FEEDBACK_RATING_TO_LABEL_MAPPING[formikBag.values.rating]}
                  </SrText>
                </SrBox>
              )}

              <SrFormikField
                formField={getFormFieldsById('tags')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />

              <SrBox width="100%" padding="20px 0">
                <SrFormikField
                  formField={getFormFieldsById('notes')}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              </SrBox>

              {formikBag.values.rating && (
                <SrButton
                  type="submit"
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  disabled={!formikBag.isValid || createFeedbackApiState.inProgress}
                >
                  {createFeedbackApiState.inProgress ? 'Loading...' : 'Submit'}
                </SrButton>
              )}
            </SrFlex>
          </Form>
        );
      }}
    />
  );
};

UPUtilityActivationFeedbackForm.propTypes = {
  utilityActivationId: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default UPUtilityActivationFeedbackForm;
